/**
 * Shared mechanics for turning backend codes into operator text.
 *
 * Each domain module declares one typed label map per backend vocabulary and
 * wraps it with `defineVocabulary`. Lookups never prettify a code: a code
 * without a label returns the domain's "not recognised" sentence, and the raw
 * code stays available for a "Technical details" disclosure.
 */

/** Shown when a status code has no operator label. */
export const UNRECOGNISED_STATUS_TEXT = 'Status not recognised';
/** Shown when a reason or blocker code has no operator label. */
export const UNRECOGNISED_REASON_TEXT = 'Reason not recognised';
/** Shown when the party that must act has no operator label. */
export const UNRECOGNISED_OWNER_TEXT = 'Who must act is not recognised';
/** Shown when the backend sent no code at all. */
export const NOT_REPORTED_TEXT = 'Not reported';

/** One looked-up code. */
export interface VocabularyTerm {
  /** Operator text: the label, or the domain's "not recognised" sentence. */
  text: string;
  /** True when the code has an operator label. */
  known: boolean;
  /** The trimmed backend code, for Technical details only; null when none was sent. */
  code: string | null;
}

export interface Vocabulary<Code extends string = string> {
  /** Short name of the backend vocabulary, used in Technical details. */
  readonly domain: string;
  /** Every code that has a label. */
  readonly codes: readonly Code[];
  /** Text shown for a code without a label. */
  readonly unrecognisedText: string;
  describe(code: string | null | undefined): VocabularyTerm;
  label(code: string | null | undefined): string;
  /** True when the code (after normalisation) has a label. */
  has(code: string | null | undefined): boolean;
}

export interface VocabularyDefinition<Code extends string> {
  domain: string;
  labels: Readonly<Record<Code, string>>;
  unrecognisedText: string;
  /** Text for an empty or missing code. Defaults to NOT_REPORTED_TEXT. */
  missingText?: string;
  /** Maps a trimmed backend code onto the label keys (for example case). */
  normalize?: (code: string) => string;
}

export const lowerCaseCode = (code: string): string => code.toLowerCase();
export const upperCaseCode = (code: string): string => code.toUpperCase();

const identityCode = (code: string): string => code;

export function defineVocabulary<Code extends string>({
  domain,
  labels,
  unrecognisedText,
  missingText = NOT_REPORTED_TEXT,
  normalize = identityCode,
}: VocabularyDefinition<Code>): Vocabulary<Code> {
  const codes = Object.keys(labels) as Code[];
  const keyFor = (code: string | null | undefined): string | null => {
    const trimmed = (code ?? '').trim();
    return trimmed ? normalize(trimmed) : null;
  };
  const has = (code: string | null | undefined): boolean => {
    const key = keyFor(code);
    return key !== null && Object.prototype.hasOwnProperty.call(labels, key);
  };
  const describe = (code: string | null | undefined): VocabularyTerm => {
    const trimmed = (code ?? '').trim();
    if (!trimmed) {
      return { text: missingText, known: false, code: null };
    }
    if (has(trimmed)) {
      return { text: labels[normalize(trimmed) as Code], known: true, code: trimmed };
    }
    return { text: unrecognisedText, known: false, code: trimmed };
  };
  return {
    domain,
    codes,
    unrecognisedText,
    describe,
    label: (code) => describe(code).text,
    has,
  };
}

/**
 * The code line for a Technical details disclosure, or null when there is
 * nothing to show. Recognised codes are included so support can match the
 * operator text to the backend value.
 */
export function technicalCodeText(
  vocabulary: Pick<Vocabulary, 'domain'>,
  term: VocabularyTerm,
): string | null {
  return term.code ? `${vocabulary.domain}: ${term.code}` : null;
}

/** Hover text for a label without a disclosure nearby: only for unrecognised codes. */
export function unrecognisedCodeTitle(term: VocabularyTerm): string | undefined {
  return !term.known && term.code ? `Code: ${term.code}` : undefined;
}
